/**
 * Protocolo de sinalizacao — unico arquivo compartilhado entre server e web.
 * So contem tipos (sem runtime), entao os dois lados importam direto do fonte.
 */

/** Alfabeto sem caracteres ambiguos (0/O, 1/I/L) usado no codigo da sala. */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const CODE_BLOCK = 4
export const CODE_BLOCKS = 2

/**
 * O que uma transmissao carrega.
 *
 * `voice` e o microfone: publicacao sem video, entao aparece na lista de
 * participantes mas nunca vira tile no palco.
 */
export type ShareKind = 'screen' | 'camera' | 'voice'

/** Tipos que viram tile: voz nao tem imagem pra mostrar. */
export const VIDEO_KINDS: ShareKind[] = ['screen', 'camera']

/** Uma pessoa pode ter no maximo uma publicacao de cada tipo. */
export const MAX_PUBLICATIONS = 3

/**
 * Uma transmissao ativa.
 *
 * Tela e camera sao publicacoes independentes: cada uma vira seu proprio tile
 * no palco, e ligar uma nao mexe na outra.
 */
export interface Publication {
    kind: ShareKind
    /**
     * Id da `MediaStream` de quem publica. O mesmo id viaja no SDP (msid), e e
     * assim que quem recebe sabe se o video que chegou e a tela ou a camera —
     * sem isso, duas trilhas do mesmo peer seriam indistinguiveis.
     */
    streamId?: string
    /** Onde assinar esta transmissao quando a sala roda em modo SFU. */
    sfu?: SfuPublication
}

export interface Participant {
    id: string
    nickname: string
    /** Foto de perfil em Data URL Base64 leve (~2KB), opcional. */
    avatar?: string
    /**
     * Dono da sala. Numa sala fixa e sempre quem a criou no Discord; numa sala
     * do momento, o primeiro a entrar. Pode tudo — inclusive promover e
     * rebaixar admins.
     */
    host: boolean
    /**
     * Moderador promovido pelo host. Expulsa convidado e promove outro
     * convidado, mas nao encosta em quem tambem modera.
     */
    admin?: boolean
    /**
     * Dono da sala FIXA no Discord — quem a criou. Diferente de `host`, que e
     * um cargo de dentro da sala e passa adiante quando alguem sai. E o que
     * diz quem pode editar nome, endereco e PIN sem sair da sala.
     */
    owner?: boolean
    /** O que esta transmitindo agora. Lista vazia = so assistindo. */
    publications: Publication[]
    joinedAt: number
    /** Microfone mutado */
    muted?: boolean
    /** Fone silenciado (deafen) */
    deafened?: boolean
    /**
     * Sub-sala em que a pessoa esta. Ausente = sala principal.
     *
     * Decide com quem a midia trafega: voz, tela e camera so vao pra quem esta
     * na MESMA sub-sala. A lista de participantes continua sendo a sala
     * inteira — e assim que da pra ver quem esta em cada uma e ir pra la.
     */
    channel?: string
}

/**
 * Sub-sala de uma sala fixa — como um canal de voz do Discord.
 *
 * So existe em sala fixa: e ela que tem endereco permanente e banco, e as
 * sub-salas ficam gravadas junto, entao amanha elas continuam la.
 */
export interface Channel {
    id: string
    name: string
    createdAt: number
}

/** Quantas sub-salas uma sala fixa pode ter. */
export const MAX_CHANNELS = 12
export const MAX_CHANNEL_NAME = 32

/** As duas pessoas estao na mesma sub-sala (ou as duas na principal)? */
export function sameChannel(a: Pick<Participant, 'channel'>, b: Pick<Participant, 'channel'>): boolean {
    return (a.channel ?? null) === (b.channel ?? null)
}

/** Coordenadas da transmissao no SFU da Cloudflare. */
export interface SfuPublication {
    sessionId: string
    /** Nomes das trilhas publicadas (video e, quando houver, audio). */
    tracks: string[]
}

export interface RoomState {
    code: string
    /** Nome da sala fixa. Sala do momento nao tem nome, so codigo. */
    name?: string
    locked: boolean
    participants: Participant[]
    /**
     * Sub-salas da sala fixa, na ordem em que foram criadas. Ausente na sala do
     * momento — e e por essa ausencia que a UI sabe que nao ha o que oferecer.
     */
    channels?: Channel[]
}

/**
 * Alguem expulso da sala.
 *
 * O IP fica so no servidor: e por ele que a volta e barrada, mas mandar isso
 * pra sala inteira seria entregar o endereco de quem saiu pra quem ficou. O
 * cliente ve um id opaco, que e o que ele devolve pra liberar a pessoa.
 */
export interface BanEntry {
    id: string
    nickname: string
    avatar?: string
    bannedAt: number
    /** Nickname de quem expulsou, pra lista dizer de quem partiu. */
    byNickname: string
}

/**
 * Endereco de uma sala fixa: e o NOME dela, virado slug.
 *
 * Sala do momento tem codigo sorteado (`XXXX-XXXX`); a fixa tem um endereco que
 * a pessoa reconhece — `/sala/daily-dos-devs` em vez de `/sala/KJ7P-2M4Q`.
 */
export const MIN_SLUG = 3
export const MAX_SLUG = 32

/**
 * "Daily dos Devs" vira "daily-dos-devs".
 *
 * Acento sai pela decomposicao (NFD): quem recebe o link copiado de um chat, ou
 * digita no celular, nao devia depender de acertar o "ã".
 */
export function slugify(raw: string): string {
    return (raw || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_SLUG)
        .replace(/-+$/g, '')
}

/**
 * Um slug nao pode ser confundivel com um codigo sorteado.
 *
 * `normalizeCode` decide o que e o que pela FORMA, sem consultar o banco. Um
 * slug de exatamente 8 caracteres usando so o alfabeto do sorteio (que ja
 * exclui 0/O/1/I/L) seria lido como sala do momento e nunca acharia a sala
 * fixa. Na pratica quase nada esbarra nisso: basta a palavra ter um i, l ou o.
 */
export function looksLikeRoomCode(value: string): boolean {
    const clean = value.replace(/-/g, '').toUpperCase()
    return clean.length === CODE_BLOCK * CODE_BLOCKS
        && [...clean].every((char) => CODE_ALPHABET.includes(char))
}

export function isRoomSlug(value: string): boolean {
    if (value.length < MIN_SLUG || value.length > MAX_SLUG) return false
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) return false
    return !looksLikeRoomCode(value)
}

/** Tamanho maximo de uma mensagem do bate-papo. */
export const MAX_CHAT_LENGTH = 500

/**
 * Quantas mensagens a sala guarda pra quem chega depois.
 *
 * Curto de proposito: e so pra quem entrou no meio nao cair num silencio sem
 * contexto. O bate-papo vive na memoria da sala e morre com ela — inclusive na
 * sala fixa, que guarda link e banimentos, mas nao conversa.
 */
export const CHAT_HISTORY = 50

export interface ChatMessage {
    id: string
    /** Id de quem enviou. `undefined` quando a pessoa ja saiu da sala. */
    from: string
    nickname: string
    avatar?: string
    text: string
    at: number
}

// ── Painel do operador ────────────────────────────────────────────────────────

/** Uma pessoa numa sala, como o painel a ve. */
export interface AdminParticipant {
    id: string
    nickname: string
    avatar?: string
    host: boolean
    admin?: boolean
    joinedAt: number
    /** So os tipos, sem as coordenadas de midia — o painel nao assiste nada. */
    publications: ShareKind[]
    muted?: boolean
    deafened?: boolean
    /** Caiu, mas o lugar segue reservado ate a carencia acabar. */
    away?: boolean
}

export interface AdminRoom {
    code: string
    /** Sala fixa tem nome e dono; sala do momento nao tem nem um nem outro. */
    name?: string
    ownerName?: string
    permanent: boolean
    requiresPin?: boolean
    locked: boolean
    /** `false` quando a sala fixa existe so no banco: ninguem dentro agora. */
    live: boolean
    createdAt: number
    lastSeenAt?: number
    participants: AdminParticipant[]
    bans: BanEntry[]
}

export interface AdminOverview {
    rooms: AdminRoom[]
    maxParticipants: number
}

/** Payload opaco de WebRTC — o servidor so encaminha, nunca inspeciona. */
export type SignalPayload =
    | { kind: 'description'; description: RTCSessionDescriptionInit }
    | { kind: 'candidate'; candidate: RTCIceCandidateInit | null }
    /** Viewer asks the sender for a lighter copy of one stream; `null` lifts the cap. */
    | { kind: 'receive-limit'; streamId: string; limit: { maxShortSide: number; maxFps: number } | null }
    /** Relay tree (docs/repasse.md): whether this viewer can forward streams to others. */
    | { kind: 'relay-capacity'; relayOk: boolean; cores: number }
    /** Viewer → publisher: send me (or stop sending) this share; nothing is encoded for who isn't watching. */
    | { kind: 'watch'; share: ShareKind; watching: boolean }
    /** Publisher → relay: forward these streams of mine to each child. */
    | { kind: 'relay-plan'; children: { id: string; streamIds: string[] }[] }
    /** Publisher → child: my streams now come through `via` (`null` = straight from me). */
    | { kind: 'relay-route'; via: string | null; streamIds: string[] }
    /** Child → publisher: route stored, the relay can start. */
    | { kind: 'relay-ack'; via: string | null }
    /** Child → publisher: these streams already arrive through the relay. */
    | { kind: 'relay-ok'; streamIds: string[] }
    /** Child → publisher: the relay failed, send straight again. */
    | { kind: 'relay-lost' }

// ── Cliente → Servidor ────────────────────────────────────────────────────────
export type ClientMessage =
    | { t: 'create'; nickname: string; avatar?: string }
    | { t: 'join'; code: string; nickname: string; pin?: string; resume?: string; avatar?: string }
    | { t: 'rename'; nickname: string; avatar?: string }
    | { t: 'avatar'; avatar?: string }
    | { t: 'lock'; locked: boolean }
    | { t: 'publish'; publications: Publication[] }
    | { t: 'audio-state'; muted?: boolean; deafened?: boolean }
    | { t: 'chat'; text: string }
    | { t: 'kick'; id: string }
    | { t: 'promote'; id: string; admin: boolean }
    | { t: 'unban'; id: string }
    | { t: 'signal'; to: string; data: SignalPayload }
    /** Cria uma sub-sala (so quem modera, so em sala fixa). */
    | { t: 'channel-create'; name: string }
    /** Apaga uma sub-sala; quem estava nela volta pra sala principal. */
    | { t: 'channel-delete'; id: string }
    /** Vai pra uma sub-sala. `null` volta pra sala principal. */
    | { t: 'channel-join'; id: string | null }
    | { t: 'leave' }
    /**
     * A aba esta indo embora — pode ser F5, link ou fechar de vez.
     *
     * Diferente de `leave`: a sala ve a pessoa sair na hora, mas o lugar dela
     * fica reservado por um par de segundos pra uma recarga reassumir o mesmo
     * id. Ver `docs/retomada-de-sessao.md`.
     */
    | { t: 'bye' }
    | { t: 'ping' }

// ── Servidor → Cliente ────────────────────────────────────────────────────────
/**
 * Pode moderar a sala (expulsar, liberar, promover)?
 *
 * O host e sempre moderador: `admin` marca so quem ele promoveu, entao checar
 * os dois juntos evita espalhar `host || admin` por toda a UI.
 */
export function canModerate(participant: Participant): boolean {
    return participant.host || !!participant.admin
}

/** Alguem esta transmitindo imagem? Voz sozinha nao conta como transmissao. */
export function isSharing(participant: Participant): boolean {
    return participant.publications.some((publication) => publication.kind !== 'voice')
}

/** Esta com o microfone na sala? */
export function isInVoice(participant: Participant): boolean {
    return participant.publications.some((publication) => publication.kind === 'voice')
}

export type ErrorCode =
    | 'room_not_found'
    | 'room_locked'
    | 'pin_required'
    | 'invalid_pin'
    | 'room_full'
    | 'invalid_nickname'
    | 'invalid_code'
    | 'not_in_room'
    | 'not_host'
    | 'not_moderator'
    | 'kicked'
    | 'banned'
    | 'room_closed'
    | 'not_permanent'
    | 'channel_not_found'
    | 'invalid_channel_name'
    | 'too_many_channels'
    | 'rate_limited'
    | 'server_busy'
    | 'bad_message'

export type ServerMessage =
    | { t: 'joined'; selfId: string; token: string; room: RoomState }
    | { t: 'peer-join'; peer: Participant }
    | { t: 'peer-leave'; id: string }
    | { t: 'peer-update'; peer: Participant }
    /**
     * So o estado de audio daquela pessoa mudou.
     *
     * Existe como mensagem propria porque mutar e desmutar o microfone e o
     * evento mais frequente de uma sala, e um `peer-update` carrega o
     * participante INTEIRO — inclusive o avatar, uns 2 a 4 KB de Data URL.
     * Numa sala de doze, cada clique no botao de microfone repetia esse avatar
     * onze vezes na rede pra mudar um booleano.
     */
    | { t: 'peer-audio'; id: string; muted: boolean; deafened: boolean }
    /** O que muda numa sala ja aberta: a tranca e, na sala fixa, o nome. */
    | { t: 'room-update'; locked: boolean; name?: string }
    /** So chega a quem modera: a lista de expulsos nao e assunto de convidado. */
    | { t: 'bans'; entries: BanEntry[] }
    /** So pra quem esta na mesma sub-sala de quem escreveu: cada uma tem seu bate-papo. */
    | { t: 'chat'; message: ChatMessage }
    /**
     * O que ja foi dito antes de voce chegar. Na entrada nao vem quando nao ha
     * nada; ao trocar de sub-sala vem sempre, mesmo vazio — e ele que tira da
     * tela a conversa da sub-sala anterior.
     */
    | { t: 'chat-history'; messages: ChatMessage[] }
    /** A lista de sub-salas mudou (criada ou apagada). */
    | { t: 'channels'; channels: Channel[] }
    | { t: 'signal'; from: string; data: SignalPayload }
    | { t: 'error'; code: ErrorCode; message: string }
    | { t: 'pong' }

/**
 * Como a midia trafega:
 * - `mesh`: cada par abre uma conexao direta (padrao, sem infra extra)
 * - `sfu`: tudo passa pelo SFU da Cloudflare (um upload por transmissor)
 */
export type MediaMode = 'mesh' | 'sfu'

export interface PublicConfig {
    iceServers: RTCIceServer[]
    maxParticipants: number
    /** Nome exibido no topo/rodape da UI. */
    brand: string
    mode: MediaMode
    /**
     * Medir e mostrar fps e bitrate de cada transmissao.
     *
     * A resolucao nao depende disto: o `<video>` avisa sozinho quando ela muda,
     * e ler `videoWidth` nao custa nada. Ja fps e bitrate saem de
     * `getStats()`, que precisa ser CHAMADO de tempos em tempos, em cada
     * conexao, no navegador de cada pessoa. E barato, mas nao e de graca — e
     * numa sala cheia sao varias conexoes por aba. A chave existe pra desligar
     * sem precisar de deploy de codigo se o custo aparecer.
     */
    streamStats: boolean
}
